import api, { ensureCsrfTokenForArgs, rtkPrepareHeadersCallback } from '@Api/Api'
import { openSnackbarAction, resetSnackbar } from '@Redux/SnackbarSlice/SnackbarSlice'
import { handleUnauthorized } from './handleUnauthorized'
import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { isBaseError } from '../Error/BaseError'
import { AxiosProgressEvent } from 'axios'
const baseQuery = fetchBaseQuery({
	baseUrl: `${process.env.REACT_APP_API_BASE_URL}`,
	prepareHeaders: rtkPrepareHeadersCallback,
	// Required for the httpOnly session cookie to travel: the portal and the API
	// are on different hosts in every environment, so without this the browser
	// sends no credential at all.
	credentials: 'include',
})

const baseQueryWithInterceptor: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
	// Unsafe methods wait for the CSRF token before leaving; GETs never do. This
	// lives here rather than in prepareHeaders because only the args carry the
	// method — several endpoints are POSTs declared as `builder.query`, so the
	// query/mutation distinction is no substitute. ProgressAPI does the same in
	// its own wrapper.
	await ensureCsrfTokenForArgs(args)
	let result = await baseQuery(args, api, extraOptions)

	// Auth failures are handled FIRST and unconditionally.
	//
	// This used to sit inside `isBaseError(...) && result.error.data.show`, so a
	// 401 whose body had show:false, or which was a FETCH_ERROR, or which did
	// not match the response envelope, never logged out at all — the app carried
	// on rendering as authenticated against a session the server had rejected.
	if (result.error && result.error.status === 401) {
		handleUnauthorized(api.dispatch, result.error)
		return result
	}

	if (result.error && isBaseError(result.error) && result.error.data.show) {
		api.dispatch(openSnackbarAction({ text: result.error.data.message, status: 'warning' }))
		setTimeout(() => {
			api.dispatch(resetSnackbar())
		}, 3000)
	}
	if (isApiResponse(result.data) && result.data.show) {
		api.dispatch(openSnackbarAction({ text: result.data.message, status: 'success' }))
		setTimeout(() => {
			api.dispatch(resetSnackbar())
		}, 3000)
	}
	if (result.error && isBaseError(result.error) && result.error.status === 307) {
		// api.dispatch(setAuthData({ redirect: result.error.data.data.redirect }))
	}
	return result
}

export const BaseApi = createApi({
	reducerPath: 'BaseApi',
	baseQuery: baseQueryWithInterceptor,
	refetchOnReconnect: true,
	// keepUnusedDataFor: 0,
	// refetchOnFocus: true,
	// refetchOnMountOrArgChange: true,

	endpoints: builder => ({
		upload: builder.query<ApiResponse<UploadResponse>, FormData>({
			query: payload => ({ url: '/upload/', method: 'POST', body: payload }),
		}),
		groups: builder.query<ApiResponse<Group[]>, number>({
			query: payload => ({ url: `/groups/${payload}` }),
		}),
		verifyPromoCode: builder.query<ApiResponse<Promocode>, { course_id: number; code: string }>({
			query: payload => ({ url: '/promocodes/verify', method: 'POST', body: payload }),
		}),
	}),
})

type UploadFileWithProgressArgs = {
	progressCallback: (progress: AxiosProgressEvent) => void
	formData: FormData
}
export const uploadFileWithProgress = async ({ formData, progressCallback }: UploadFileWithProgressArgs) => {
	return await api.post<ApiResponse<UploadResponse>>('/upload', formData, {
		onUploadProgress(progressEvent) {
			progressCallback(progressEvent)
		},
	})
}
export const { useLazyUploadQuery, useGroupsQuery, useLazyVerifyPromoCodeQuery } = BaseApi

export function isApiResponse(value: unknown): value is ApiResponse<any> {
	return typeof value === 'object' && value !== null && 'message' in value && 'data' in value
}
